import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import AuthCard from 'src/features/auth/components/auth-card'
import AuthContent from 'src/features/auth/components/auth-content'
import AuthSubmitButton from 'src/features/auth/components/auth-submit-button'
import AuthSubtitle from 'src/features/auth/components/auth-subtitle'
// import AuthSwitchFlow from 'src/features/auth/components/auth-switch-flow'
import AuthTitle from 'src/features/auth/components/auth-title'
import { Form } from 'src/shared/components/ui/forms/form'
import { useSignIn } from 'src/shared/features/auth/api'
import { useAddToCognitoGroup } from 'src/shared/features/auth/api/use-add-to-cognito-group'
import AuthInputFormField from 'src/shared/features/auth/components/auth-input-form-field'
import { useToast } from 'src/shared/hooks/use-toast'
import type { LoginFormData } from 'src/shared/schemas/forms/auth'
import { loginSchema } from 'src/shared/schemas/forms/auth'

interface ProfessionalLoginFormProps {
  onSuccess: () => void
  handleForgotPassword: () => void
}

const ProfessionalLoginForm = ({ onSuccess, handleForgotPassword }: ProfessionalLoginFormProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const signIn = useSignIn()
  const { mutateAsync: addToCognitoGroup } = useAddToCognitoGroup()
  const { toast } = useToast()

  const onLogin = async (data: LoginFormData) => {
    setIsLoading(true)
    try {
      await signIn.mutateAsync({
        email: data.email,
        password: data.password,
      })
      try {
        await addToCognitoGroup()
      } catch (error) {
        console.error(error)
      }

      // if (response.result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
      //   // Successfully signed in, navigate to confirm account
      //   navigate(paths.auth.confirmAccount.getHref(data.email, redirectTo || ''))
      // } else if (response.result.isSignedIn) {
      //   // Successfully signed in, navigate to home
      //   navigate(`${redirectTo ? `${redirectTo}` : paths.home.getHref()}`, {
      //     replace: true,
      //   })
      // }
      onSuccess()
    } catch (error) {
      console.error(error)
      let errorMessage = 'An unknown error occurred'
      if (error instanceof Error) {
        if (error.name === 'TooManyRequestsException') {
          errorMessage = "There's a problem with our connection, please try again later"
        } else if (error.name === 'UserAlreadyAuthenticatedException') {
          // User is already authenticated, redirect them
          // navigate(`${redirectTo ? `${redirectTo}` : paths.home.getHref()}`, {
          //   replace: true,
          // })
          return setIsLoading(false)
        } else {
          errorMessage = error.message
        }
      }

      toast({
        variant: 'destructive',
        title: 'Login failed',
        description: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthCard contentClassName="h-fit py-0 md:py-0" className="px-0 md:px-0">
      <AuthContent>
        <AuthTitle title="Welcome back! 👋" />
        <AuthSubtitle description="Where homeownership happens." />
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onLogin)} className="space-y-5">
            <div className="flex flex-col gap-2">
              <AuthInputFormField control={form.control} name="email" label="Email" type="email" />
              <AuthInputFormField control={form.control} name="password" label="Password" type="password" />
            </div>
            <div
              onClick={handleForgotPassword}
              className="mt-2 min-h-0 w-fit p-0 text-xs text-muted hover:cursor-pointer hover:underline"
            >
              Forgot your password?
            </div>
            <AuthSubmitButton type="submit" className="mt-4" isLoading={signIn.isPending || isLoading}>
              {signIn.isPending || isLoading ? 'Signing in...' : 'Sign in'}
            </AuthSubmitButton>
          </form>
        </Form>
      </AuthContent>
    </AuthCard>
  )
}

export default ProfessionalLoginForm
